import { BreakpointObserver } from '@angular/cdk/layout';
import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  Output,
  QueryList,
  ViewChildren,
  OnInit,
  OnDestroy,
} from '@angular/core';
import { NgModel } from '@angular/forms';
import { Router } from '@angular/router';
import { RatePlan } from '@app/shared/interfaces/lib/rate-plan.interface';
import { BookingFlowService } from '@app/shared/services/booking-flow/booking-flow.service';
import { ConfirmationService } from '@app/shared/services/confirmation/confirmation.service';
import { MarkupsService } from '@app/features/pricing-and-availability';
import { SettingsService } from '@app/shared/services/settings/settings.service';
import { isNullOrUndefined } from '@app/shared/utils';
import { Subject, forkJoin } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';

interface PropertyResponse {
  property_id: string;
  name: string;
  address: string;
  image: string | null;
  listings: ListingResponse[];
}

interface ListingResponse {
  listing_id: string;
  name: string;
  currency: string;
  rate_plans: RatePlan[];
}

interface PropertyRequest {
  property_id: string;
  listings: ListingRequest[];
}

interface ListingRequest {
  listing_id: string;
  ratePlanUuid: string;
  isDerived: boolean;
  extraGuestFee: number;
  guestsIncluded: number;
  maxOccupancy: number;
}

@Component({
  standalone: false,
  selector: 'sbnb-connect-pricing',
  templateUrl: './connect-booking-pricing.component.html',
  styleUrls: ['./connect-booking-pricing.component.scss'],
})
export class ConnectBookingPricingComponent implements AfterViewInit, OnInit, OnDestroy {
  @Input() platform = 'booking';
  @Input() propertyIds: any[];
  @Output() stepComplete: EventEmitter<boolean> = new EventEmitter();
  @Output() noPropertyIdsEmitter: EventEmitter<any> = new EventEmitter();
  @ViewChildren('maxOccupancyDesktop, guestsIncludedDesktop, extraGuestFeeDesktop') desktopFields: QueryList<NgModel>;
  @ViewChildren('maxOccupancyMobile, guestsIncludedMobile, extraGuestFeeMobile') mobileFields: QueryList<NgModel>;

  markupLoading: boolean;
  pricingLoading: boolean;
  saving: boolean;
  propertiesResponse: PropertyResponse[] = []; // Initial data from BE
  propertiesRequest: PropertyRequest[] = []; // To send back to BE

  isMobile = false;
  private destroyed$ = new Subject<void>();

  constructor(
    private readonly markupsService: MarkupsService,
    private readonly router: Router,
    private readonly settingsService: SettingsService,
    private readonly breakpointObserver: BreakpointObserver,
    private confirmationService: ConfirmationService,
    private bookingFlowService: BookingFlowService
  ) {
    this.confirmationService.setConfirmation(
      'Are you sure?\nYour rates may not be correctly setup if if you exit now.',
      () => {
        return confirm('Are you sure?\nYour rates may not be correctly setup if if you exit now.');
      }
    );
  }

  ngAfterViewInit(): void {}

  markup: number;
  placeholder_markup: string;
  default_markup = '18';
  bookingcom_markup = 17.5;

  ngOnInit(): void {
    this.breakpointObserver
      .observe(['(max-width: 1279px)'])
      .pipe(
        map((result) => result.matches),
        takeUntil(this.destroyed$)
      )
      .subscribe((isMobile) => {
        this.isMobile = isMobile;
      });

    if (this.propertyIds.length === 0) {
      if (this.bookingFlowService.propertyIds.length > 0) {
        this.propertyIds = this.bookingFlowService.propertyIds;
      }
    }

    if (this.propertyIds.length === 0) {
      this.noPropertyIdsEmitter.emit();
      return;
    }

    this.markupLoading = true;
    this.pricingLoading = true;

    const email = localStorage.getItem('account_login');

    this.markupsService.getPlatformMarkups().subscribe(
      (res) => {
        this.markupLoading = false;

        const platform = res.find((x) => x.platform === this.platform);

        if (!isNullOrUndefined(platform.markup)) {
          this.placeholder_markup = (platform.markup * 100).toString();

          if (this.platform === 'booking') {
            this.markup = platform.markup * 100;
          }
        } else {
          this.placeholder_markup = this.default_markup;

          if (this.platform === 'booking') {
            this.placeholder_markup = this.bookingcom_markup.toString();
            this.markup = this.bookingcom_markup;
          }
        }
      },
      (err) => {
        this.markupLoading = false;
      }
    );

    this.settingsService.getOccupancyBasedPricingSettings(this.propertyIds, 'booking', email).subscribe(
      (res) => {
        this.pricingLoading = false;

        if (res.error) {
          return;
        }

        this.propertiesResponse = res.data;
        this.processProperties();

        // Validate so we can show first hand the fields with errors
        setTimeout(() => this.validateForm(false), 0);
      },
      (err) => {
        this.pricingLoading = false;
      }
    );
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
    this.confirmationService.clearConfirmation();
  }

  processProperties() {
    this.propertiesResponse = this.propertiesResponse.filter((propertyResponse) => {
      // Has a derived rate plan?
      const hasDerived = propertyResponse.listings.some((listing) =>
        listing.rate_plans.some((plan) => plan.is_derived)
      );
      if (hasDerived) {
        return true;
      }

      // Has more than one rate plans?
      const hasMultipleRatePlans = propertyResponse.listings.some((listing) => listing.rate_plans.length > 1);
      if (hasMultipleRatePlans) {
        return true;
      }

      return false;
    });

    this.propertiesResponse.forEach((propertyResponse) => {
      const propertyRequest: PropertyRequest = {
        property_id: propertyResponse.property_id,
        listings: propertyResponse.listings.map((listing) => {
          const primaryRatePlan = listing.rate_plans.find((plan) => plan.is_primary === true);
          const extraGuestFee = primaryRatePlan?.derived_settings?.extra_guest_fee;

          return {
            listing_id: listing.listing_id,
            ratePlanUuid: primaryRatePlan ? primaryRatePlan.uuid : '',
            isDerived: primaryRatePlan ? primaryRatePlan.is_derived : false,
            extraGuestFee: extraGuestFee && extraGuestFee.type === 'fixed' ? extraGuestFee.value : null,
            guestsIncluded: primaryRatePlan?.derived_settings?.guests_included,
            maxOccupancy: primaryRatePlan?.derived_settings?.max_occupancy,
          } as ListingRequest;
        }),
      };

      this.propertiesRequest.push(propertyRequest);
    });
  }

  onRatePlanChange(newratePlanUuid: string, propertyIndex: number, listingIndex: number): void {
    const listingRequest = this.propertiesRequest[propertyIndex].listings[listingIndex];
    const selectedRatePlan = this.propertiesResponse[propertyIndex].listings[listingIndex].rate_plans.find(
      (plan) => plan.uuid === newratePlanUuid
    );

    listingRequest.isDerived = selectedRatePlan?.is_derived;

    if (listingRequest.isDerived) {
      const ratePlanExtraGuestFee = selectedRatePlan?.derived_settings?.extra_guest_fee;
      listingRequest.extraGuestFee =
        ratePlanExtraGuestFee && ratePlanExtraGuestFee.type === 'fixed' ? ratePlanExtraGuestFee.value : null;

      const ratePlanGuestsIncluded = selectedRatePlan?.derived_settings?.guests_included;
      listingRequest.guestsIncluded = ratePlanGuestsIncluded ?? null;

      const ratePlanMaxOccupancy = selectedRatePlan?.derived_settings?.max_occupancy;
      listingRequest.maxOccupancy = ratePlanMaxOccupancy ?? null;
    } else {
      listingRequest.extraGuestFee = null;
      listingRequest.guestsIncluded = null;
      listingRequest.maxOccupancy = null;
    }

    // Re-validate everything
    this.validateForm(false);
  }

  progress() {
    if (!this.markup) {
      return;
    }

    if (!this.validateForm()) {
      return;
    }

    this.saving = true;

    const email = localStorage.getItem('account_login');
    if (!email) {
      return;
    }

    const updateMarkup$ = this.markupsService.updatePlatformMarkups([
      {
        platform: this.platform,
        markup: this.markup / 100,
      },
    ]);

    const forms = [updateMarkup$];

    if (this.propertiesResponse.length > 0) {
      const updatePricing$ = this.settingsService.setOccupancyBasedPricingSettings(
        this.propertiesRequest,
        'booking',
        email
      );
      forms.push(updatePricing$);
    }

    forkJoin(forms).subscribe({
      next: ([markupResponse, pricingResponse]) => {
        this.saving = false;

        if (markupResponse?.error || pricingResponse?.error) {
          return;
        }

        this.stepComplete.emit(true);
      },
      error: (err) => {
        this.saving = false;
      },
    });
  }

  validateForm(focus = true): boolean {
    const fields = this.isMobile ? this.mobileFields : this.desktopFields;

    this.markAllAsTouched(fields);

    const firstInvalidControl = this.getFirstInvalidControl(fields);

    if (firstInvalidControl) {
      if (focus) {
        setTimeout(() => firstInvalidControl.valueAccessor['_elementRef'].nativeElement.focus(), 0);
      }
      return false;
    }

    return true;
  }

  markAllAsTouched(fields: QueryList<NgModel>): void {
    fields.forEach((control: NgModel) => {
      if (control.control.disabled) {
        return;
      }
      control.control.markAsTouched();
    });
  }

  getFirstInvalidControl(fields: QueryList<NgModel>): NgModel | null {
    return fields.find((control) => !control.valid && !control.control.disabled) || null;
  }
}
